import type { Profile } from "./supabase";

const OFFLINE_AUTH_KEY = "qashup_owner_offline_auth_v1";

interface OwnerAuthCache {
  email: string;     // lower-cased
  hash: string;      // SHA-256 of password
  profile: string;   // JSON-serialised Profile
  access_token: string;
  refresh_token: string;
}

async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
}

export async function cacheOwnerOfflineAuth(
  email: string,
  password: string,
  profile: Profile,
  access_token: string,
  refresh_token: string,
): Promise<void> {
  const hash = await sha256(password);
  const entry: OwnerAuthCache = {
    email: email.toLowerCase(),
    hash,
    profile: JSON.stringify(profile),
    access_token,
    refresh_token,
  };
  localStorage.setItem(OFFLINE_AUTH_KEY, JSON.stringify(entry));
}

export async function verifyOwnerOfflineCredentials(
  email: string,
  password: string,
): Promise<{ profile: Profile; access_token: string; refresh_token: string } | null> {
  try {
    const raw = localStorage.getItem(OFFLINE_AUTH_KEY);
    if (!raw) return null;
    const entry: OwnerAuthCache = JSON.parse(raw);
    if (entry.email !== email.toLowerCase()) return null;
    const hash = await sha256(password);
    if (entry.hash !== hash) return null;
    return {
      profile: JSON.parse(entry.profile) as Profile,
      access_token: entry.access_token,
      refresh_token: entry.refresh_token,
    };
  } catch {
    return null;
  }
}

export function updateOwnerCachedTokens(access_token: string, refresh_token: string): void {
  try {
    const raw = localStorage.getItem(OFFLINE_AUTH_KEY);
    if (!raw) return;
    const entry: OwnerAuthCache = JSON.parse(raw);
    entry.access_token = access_token;
    entry.refresh_token = refresh_token;
    localStorage.setItem(OFFLINE_AUTH_KEY, JSON.stringify(entry));
  } catch { /* ignore */ }
}

export function clearOwnerOfflineAuth(): void {
  localStorage.removeItem(OFFLINE_AUTH_KEY);
}
